import { MAX_GAP_MS } from "./accumulate.js";
import { ADVECT_THRESHOLD_MS, sampleFlow } from "./flow.js";

const MS_PER_HOUR = 3600 * 1000;
const LINEAR_STEP_MS = 60 * 1000;

function sampleBilinear(rate, nx, ny, x, y) {
  if (!(x >= 0 && y >= 0 && x < nx - 1 && y < ny - 1)) return 0;
  const ix = x | 0;
  const iy = y | 0;
  const gx = x - ix;
  const gy = y - iy;
  const q = iy * nx + ix;
  const c = (i) => (rate[i] > 0 ? rate[i] : 0);
  const top = c(q) + gx * (c(q + 1) - c(q));
  const bot = c(q + nx) + gx * (c(q + nx + 1) - c(q + nx));
  return top + gy * (bot - top);
}

// Rain rate and running total at grid cells, using the same math as
// linearPairIntegrator / createAdvectionIntegrator so each final cumulative
// value equals the gridded total at that cell. Frames are unpacked once for
// all cells. Returns one entry per cell, in mm/h and mm:
// { rate: {t, v}, cum: {t, v}, observed: {t, v}, gaps, total }.
// Gaps appear as NaN breaks in both series.
export function pointSeriesMany({
  frames,
  flows = [],
  grid,
  cells,
  startMs,
  endMs,
  maxGapMs = MAX_GAP_MS,
  thresholdMs = ADVECT_THRESHOLD_MS,
  maxSubsteps = 30,
}) {
  const { nx, ny } = grid;
  const getRate = (f) => (typeof f.rate === "function" ? f.rate() : f.rate);
  const push = (s, t, v) => {
    s.t.push(t);
    s.v.push(v);
  };
  const out = cells.map(({ col, row }) => ({
    col,
    row,
    i: row * nx + col,
    rate: { t: [], v: [] },
    cum: { t: [], v: [] },
    observed: { t: [], v: [] },
    total: 0,
    covered: false,
    outside: false,
  }));
  const gaps = [];

  let prev = null;
  let prevIndex = -1;
  for (let p = 0; p + 1 < frames.length; p++) {
    const a = frames[p];
    const b = frames[p + 1];
    const t0 = Math.max(a.tMs, startMs);
    const t1 = Math.min(b.tMs, endMs);
    if (t1 <= t0) continue;
    const dt = b.tMs - a.tMs;
    if (dt > maxGapMs) {
      gaps.push({ startMs: t0, endMs: t1 });
      for (const c of out) {
        push(c.rate, t0, NaN);
        push(c.cum, t0, NaN);
      }
      continue;
    }
    const r0 = prevIndex === p ? prev : getRate(a);
    const r1 = getRate(b);
    prev = r1;
    prevIndex = p + 1;
    const a0 = (t0 - a.tMs) / dt;
    const a1 = (t1 - a.tMs) / dt;
    const span = a1 - a0;
    const hours = dt / MS_PER_HOUR;
    const entry = flows[p];
    const flow = typeof entry === "function" ? entry() : entry;
    const linear = !flow || !(flow.maxDisp * span > 0.05) || Math.round(hours * MS_PER_HOUR) <= thresholdMs;

    for (const c of out) {
      if (c.outside) continue;
      const { i, col, row, rate, cum, observed } = c;
      if (!Number.isFinite(r0[i]) || !Number.isFinite(r1[i])) {
        c.outside = true;
        continue;
      }
      c.covered = true;
      if (a.tMs >= startMs) push(observed, a.tMs, r0[i]);
      if (b.tMs <= endMs) push(observed, b.tMs, r1[i]);
      if (cum.t.length === 0 || Number.isNaN(cum.v[cum.v.length - 1])) push(cum, t0, c.total);

      if (linear) {
        // Linear blend: exact trapezoids between evenly spaced samples.
        const steps = Math.max(1, Math.ceil((t1 - t0) / LINEAR_STEP_MS));
        const at = (alpha) => (1 - alpha) * r0[i] + alpha * r1[i];
        let lastRate = at(a0);
        push(rate, t0, lastRate);
        for (let k = 1; k <= steps; k++) {
          const alpha = a0 + (span * k) / steps;
          const r = at(alpha);
          c.total += ((lastRate + r) / 2) * (span / steps) * hours;
          lastRate = r;
          push(rate, a.tMs + alpha * dt, r);
          push(cum, a.tMs + alpha * dt, c.total);
        }
        continue;
      }

      const [u, v] = sampleFlow(flow, col, row);
      const steps = Math.min(maxSubsteps, Math.max(1, Math.ceil(Math.sqrt(u * u + v * v) * span)));
      const da = span / steps;
      for (let k = 0; k < steps; k++) {
        const alpha = a0 + (k + 0.5) * da;
        const r = (1 - alpha) * sampleBilinear(r0, nx, ny, col - alpha * u, row - alpha * v)
          + alpha * sampleBilinear(r1, nx, ny, col + (1 - alpha) * u, row + (1 - alpha) * v);
        c.total += r * da * hours;
        push(rate, a.tMs + alpha * dt, r);
        push(cum, a.tMs + (a0 + (k + 1) * da) * dt, c.total);
      }
    }
  }

  return out.map((c) => {
    const seen = new Set();
    const observed = { t: [], v: [] };
    c.observed.t.forEach((t, k) => {
      if (seen.has(t)) return;
      seen.add(t);
      push(observed, t, c.observed.v[k]);
    });
    const total = c.outside || !c.covered ? NaN : c.total;
    return c.outside
      ? { rate: { t: [], v: [] }, cum: { t: [], v: [] }, observed: { t: [], v: [] }, gaps, total }
      : { rate: c.rate, cum: c.cum, observed, gaps, total };
  });
}

export function pointSeries({ col, row, ...opts }) {
  return pointSeriesMany({ ...opts, cells: [{ col, row }] })[0];
}

// Running total at time t, interpolated between cumulative samples. Before
// the first sample it is 0; inside a data gap it holds the last value.
export function cumAt(cum, t) {
  let last = 0;
  for (let k = 0; k < cum.t.length; k++) {
    const v = cum.v[k];
    if (!Number.isFinite(v)) continue;
    if (cum.t[k] >= t) {
      const kPrev = k - 1;
      if (kPrev < 0 || !Number.isFinite(cum.v[kPrev]) || cum.t[k] === cum.t[kPrev]) return cum.t[k] === t ? v : last;
      const f = (t - cum.t[kPrev]) / (cum.t[k] - cum.t[kPrev]);
      return cum.v[kPrev] + f * (v - cum.v[kPrev]);
    }
    last = v;
  }
  return last;
}
